/**
 * AI 잡 큐 모니터링 — admin 시스템 페이지
 *
 * - 메인 큐 / DLQ 깊이 (대기/처리중/지연)
 * - 최근 batch_job N개 (DB 기반 — payload·진행률 포함, 행 클릭 시 펼침)
 * - 워커 Lambda 최근 5분 로그 50줄
 * - DLQ peek (최대 10개, 30s visibility — 삭제 안 함)
 * - 테스트 메시지 enqueue 버튼
 * - 자동 갱신 토글 + 주기 선택 (3/5/10초, localStorage 저장)
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { Activity, RefreshCw, Send, Inbox, Play, Pause, ChevronDown, ChevronRight } from 'lucide-react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Button, Spinner, Badge, Select } from '@/ui'
import styles from './AiQueueMonitor.module.css'

interface QueueDepth {
  name: string
  visible: number
  inFlight: number
  delayed: number
}

interface LogEvent {
  timestamp: number
  message: string
}

interface StatusResponse {
  queues: { main: QueueDepth; dlq: QueueDepth }
  recentLogs: LogEvent[]
  workerLogGroup: string | null
}

interface DlqMessage {
  messageId?: string
  body?: string
  sentTimestamp: number | null
  receiveCount: number | null
}

interface RecentJob {
  id: string
  userId: string | null
  userEmail: string | null
  userName: string | null
  status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled' | string
  modelId: string | null
  kind: string | null
  totalImages: number
  completedImages: number
  failedImages: number
  creditsReserved: number
  creditsUsed: number
  albumId: string | null
  createdAt: string
  completedAt: string | null
  payload: Record<string, unknown> | null
  generationCount: number
}

const REFRESH_OPTIONS = [
  { value: '3000', label: '3초' },
  { value: '5000', label: '5초' },
  { value: '10000', label: '10초' },
]

const STORAGE_KEY = 'ai-queue-monitor:refresh'

interface RefreshSettings {
  enabled: boolean
  intervalMs: number
}

function loadRefreshSettings(): RefreshSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<RefreshSettings>
      return {
        enabled: parsed.enabled ?? true,
        intervalMs: parsed.intervalMs ?? 5_000,
      }
    }
  } catch {
    // ignore
  }
  return { enabled: true, intervalMs: 5_000 }
}

function saveRefreshSettings(s: RefreshSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch {
    // ignore
  }
}

function formatTimestamp(ms: number): string {
  if (!ms) return '-'
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function durationLabel(start: string, end: string | null): string {
  const startMs = new Date(start).getTime()
  const endMs = end ? new Date(end).getTime() : Date.now()
  const diffSec = Math.max(0, Math.round((endMs - startMs) / 1000))
  if (diffSec < 60) return `${diffSec}초`
  const m = Math.floor(diffSec / 60)
  const s = diffSec % 60
  return `${m}분 ${s}초`
}

const STATUS_BADGE: Record<string, { color: 'gray' | 'blue' | 'green' | 'yellow' | 'red'; label: string }> = {
  pending: { color: 'gray', label: '대기' },
  processing: { color: 'blue', label: '처리 중' },
  completed: { color: 'green', label: '완료' },
  partial: { color: 'yellow', label: '부분 실패' },
  failed: { color: 'red', label: '실패' },
  cancelled: { color: 'gray', label: '취소' },
}

export function AiQueueMonitor() {
  const { addToast } = useToast()
  const [status, setStatus] = useState<StatusResponse | null>(null)
  const [recentJobs, setRecentJobs] = useState<RecentJob[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [dlqMessages, setDlqMessages] = useState<DlqMessage[] | null>(null)
  const [isPeeking, setIsPeeking] = useState(false)
  const [isEnqueueing, setIsEnqueueing] = useState(false)
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)
  const [refresh, setRefresh] = useState<RefreshSettings>(() => loadRefreshSettings())

  const loadStatus = useCallback(async () => {
    try {
      const [statusData, jobsData] = await Promise.all([
        apiFetch<StatusResponse>('/api/admin/ai-queue/status'),
        apiFetch<{ jobs: RecentJob[] }>('/api/admin/ai-queue/recent-jobs?limit=30'),
      ])
      setStatus(statusData)
      setRecentJobs(jobsData.jobs)
      setError(null)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '알 수 없는 오류'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // 초기 로드
  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  // 자동 갱신 — refresh 설정 변경 시 재구성
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    if (!refresh.enabled) return
    const id = setInterval(() => {
      // 최신 설정 기준으로 호출 (clearInterval 후 재설정 패턴 대신 ref 사용)
      if (refreshRef.current.enabled) loadStatus()
    }, refresh.intervalMs)
    return () => clearInterval(id)
  }, [refresh.enabled, refresh.intervalMs, loadStatus])

  // refresh 설정 변경 → localStorage 저장
  useEffect(() => {
    saveRefreshSettings(refresh)
  }, [refresh])

  const handleDlqPeek = useCallback(async () => {
    setIsPeeking(true)
    try {
      const data = await apiFetch<{ messages: DlqMessage[] }>('/api/admin/ai-queue/dlq-peek')
      setDlqMessages(data.messages)
      addToast({
        type: 'success',
        message: `DLQ 메시지 ${data.messages.length}개 조회 (30초 후 다시 큐에 보임)`,
      })
    } catch (e: unknown) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : 'DLQ peek 실패' })
    } finally {
      setIsPeeking(false)
    }
  }, [addToast])

  const handleTestEnqueue = useCallback(async () => {
    setIsEnqueueing(true)
    try {
      const data = await apiFetch<{ jobId: string; messageId: string }>(
        '/api/admin/ai-queue/test-enqueue',
        { method: 'POST', body: JSON.stringify({}) },
      )
      addToast({
        type: 'success',
        message: `테스트 메시지 전송: ${data.jobId} (워커가 "잡 없음 — 폐기" 로그 찍어야 정상)`,
      })
      setTimeout(loadStatus, 3000)
    } catch (e: unknown) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : '테스트 전송 실패' })
    } finally {
      setIsEnqueueing(false)
    }
  }, [addToast, loadStatus])

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Spinner /> 큐 상태 로드 중...
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <Activity size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          AI 잡 큐 모니터링
        </h1>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={() => setRefresh((r) => ({ ...r, enabled: !r.enabled }))}
            title={refresh.enabled ? '자동 갱신 정지' : '자동 갱신 시작'}
          >
            {refresh.enabled ? <Pause size={14} /> : <Play size={14} />}
            {refresh.enabled ? ' 정지' : ' 시작'}
          </Button>
          <Select
            size="sm"
            value={String(refresh.intervalMs)}
            onChange={(e) => setRefresh((r) => ({ ...r, intervalMs: Number(e.target.value) }))}
            options={REFRESH_OPTIONS}
            disabled={!refresh.enabled}
            aria-label="자동 갱신 주기"
          />
          <Button variant="secondary" onClick={loadStatus}>
            <RefreshCw size={14} /> 새로고침
          </Button>
          <Button variant="secondary" onClick={handleTestEnqueue} disabled={isEnqueueing}>
            <Send size={14} /> 테스트 enqueue
          </Button>
        </div>
      </div>

      {error && <div className={styles.errorBanner}>오류: {error}</div>}

      {status && (
        <>
          <div className={styles.queueGrid}>
            <QueueCard title="메인 큐" depth={status.queues.main} />
            <QueueCard title="DLQ" depth={status.queues.dlq} alert={status.queues.dlq.visible > 0} />
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <span>최근 배치 잡 (DB)</span>
              <span className={styles.refreshHint}>
                {recentJobs?.length ?? 0}개 — 행 클릭 시 payload 펼침
              </span>
            </h2>
            <RecentJobsTable
              jobs={recentJobs ?? []}
              expandedJobId={expandedJobId}
              onToggle={(id) => setExpandedJobId((prev) => (prev === id ? null : id))}
            />
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <span>
                <Inbox size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                DLQ 메시지 peek
              </span>
              <Button variant="secondary" onClick={handleDlqPeek} disabled={isPeeking}>
                {isPeeking ? <Spinner /> : <Inbox size={14} />} DLQ 메시지 보기
              </Button>
            </div>
            {dlqMessages !== null && (
              <DlqTable messages={dlqMessages} />
            )}
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <span>워커 Lambda 최근 5분 로그</span>
              {status.workerLogGroup && (
                <span className={styles.refreshHint}>{status.workerLogGroup}</span>
              )}
            </h2>
            <LogFeed logs={status.recentLogs} />
          </div>
        </>
      )}
    </div>
  )
}

function QueueCard({ title, depth, alert }: { title: string; depth: QueueDepth; alert?: boolean }) {
  return (
    <div className={styles.queueCard}>
      <div className={styles.queueCardHeader}>
        <h2 className={styles.queueCardTitle}>{title}</h2>
        {alert && <Badge color="red">주의</Badge>}
        <span className={styles.queueCardSubtitle}>{depth.name}</span>
      </div>
      <div className={styles.queueStats}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>대기</div>
          <div className={`${styles.statValue} ${alert ? styles.statValueAlert : ''}`}>
            {depth.visible}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>처리 중</div>
          <div className={styles.statValue}>{depth.inFlight}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>지연</div>
          <div className={styles.statValue}>{depth.delayed}</div>
        </div>
      </div>
    </div>
  )
}

function RecentJobsTable({
  jobs,
  expandedJobId,
  onToggle,
}: {
  jobs: RecentJob[]
  expandedJobId: string | null
  onToggle: (id: string) => void
}) {
  if (!jobs.length) {
    return <div className={styles.logEmpty}>최근 배치 잡 없음</div>
  }
  return (
    <div className={styles.jobsTable}>
      <div className={`${styles.jobRow} ${styles.jobRowHeader}`}>
        <div></div>
        <div>시각</div>
        <div>jobId</div>
        <div>kind</div>
        <div>유저</div>
        <div>상태</div>
        <div>진행률</div>
        <div>모델</div>
        <div>실행</div>
      </div>
      {jobs.map((j) => {
        const isExpanded = expandedJobId === j.id
        const badge = STATUS_BADGE[j.status] ?? { color: 'gray' as const, label: j.status }
        const total = j.totalImages || 1
        const done = j.completedImages
        const failed = j.failedImages
        return (
          <div key={j.id}>
            <div
              className={`${styles.jobRow} ${styles.jobRowClickable}`}
              onClick={() => onToggle(j.id)}
            >
              <div>
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
              <div className={styles.jobMono}>{formatDateTime(j.createdAt)}</div>
              <div className={styles.jobMono} title={j.id}>{j.id.slice(0, 10)}…</div>
              <div className={styles.jobMono}>{j.kind ?? '-'}</div>
              <div title={j.userEmail ?? j.userId ?? ''}>
                {j.userName ?? j.userEmail ?? j.userId?.slice(0, 8) ?? '-'}
              </div>
              <div>
                <Badge color={badge.color}>{badge.label}</Badge>
              </div>
              <div>
                <span className={styles.progressText}>
                  {done}/{total}
                  {failed > 0 && <span className={styles.failedHint}> ({failed}실패)</span>}
                </span>
                <div className={styles.progressBarOuter}>
                  <div
                    className={styles.progressBarInner}
                    style={{ width: `${Math.min(100, Math.round((done / total) * 100))}%` }}
                  />
                </div>
              </div>
              <div className={styles.jobMono}>{j.modelId ?? '-'}</div>
              <div className={styles.jobMono}>{durationLabel(j.createdAt, j.completedAt)}</div>
            </div>
            {isExpanded && (
              <div className={styles.jobDetails}>
                <div className={styles.jobDetailsMeta}>
                  <span>userId: <code>{j.userId ?? '-'}</code></span>
                  <span>email: <code>{j.userEmail ?? '-'}</code></span>
                  <span>albumId: <code>{j.albumId ?? '-'}</code></span>
                  <span>creditsReserved: <code>{j.creditsReserved}</code></span>
                  <span>creditsUsed: <code>{j.creditsUsed}</code></span>
                  <span>generation 행: <code>{j.generationCount}</code></span>
                  <span>완료: <code>{j.completedAt ? formatDateTime(j.completedAt) : '-'}</code></span>
                </div>
                <div className={styles.jobDetailsLabel}>payload</div>
                <pre className={styles.jobPayload}>
                  {j.payload ? JSON.stringify(j.payload, null, 2) : '(payload 없음 — 큐 핸드오프 이전 잡)'}
                </pre>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function LogFeed({ logs }: { logs: LogEvent[] }) {
  if (!logs.length) {
    return (
      <div className={styles.logFeed}>
        <div className={styles.logEmpty}>최근 5분간 워커 로그 없음</div>
      </div>
    )
  }
  return (
    <div className={styles.logFeed}>
      {logs.map((log, i) => (
        <div key={`${log.timestamp}-${i}`} className={styles.logRow}>
          <span className={styles.logTime}>{formatTimestamp(log.timestamp)}</span>
          <span>{log.message.trim()}</span>
        </div>
      ))}
    </div>
  )
}

function DlqTable({ messages }: { messages: DlqMessage[] }) {
  if (!messages.length) {
    return <div className={styles.logEmpty}>DLQ 메시지 없음 ✓</div>
  }
  return (
    <div className={styles.dlqTable}>
      <div className={`${styles.dlqRow} ${styles.dlqRowHeader}`}>
        <div>본문</div>
        <div>받은 시각</div>
        <div>수신 횟수</div>
      </div>
      {messages.map((m) => (
        <div key={m.messageId} className={styles.dlqRow}>
          <div className={styles.dlqBody} title={m.body}>{m.body}</div>
          <div>{m.sentTimestamp ? formatTimestamp(m.sentTimestamp) : '-'}</div>
          <div>{m.receiveCount ?? '-'}</div>
        </div>
      ))}
    </div>
  )
}
